import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { extractApiError } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PartnerSignup — direct self-signup. No admin approval at this step.
 * On success, the new partner can log into /portal/login and submit KYC.
 * Their partner_code is generated only after an admin approves their KYC.
 */
export default function PartnerSignup() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: "",
    city: "",
    category: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = () =>
    form.full_name.trim().length > 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    /^\+?[\d\s-]{10,15}$/.test(form.phone) &&
    form.password.length >= 8;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid()) return toast.error("Please fill all required fields correctly.");
    setBusy(true);
    try {
      await axios.post(`${API}/public/partner/signup`, {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.replace(/\D/g, ""),
        password: form.password,
        city: form.city.trim() || null,
        category: form.category || null,
      });
      setDone(true);
      toast.success("Partner account created — sign in to continue");
    } catch (err) {
      toast.error(extractApiError(err, "Signup failed — please try again"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16 flex justify-center" data-testid="partner-signup-done">
        <Seo title="Partner account created" />
        <Card className="max-w-lg w-full p-8 text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
          <h1 className="font-display text-2xl font-bold mt-4">Account created!</h1>
          <p className="text-slate-700 mt-2 leading-relaxed">
            Sign in with your email &amp; password, then complete your KYC from the dashboard. We&apos;ll review your details and unlock your unique partner code within 24-48 hours.
          </p>
          <Button onClick={() => nav("/portal/login")} className="mt-6 bg-slate-900 hover:bg-slate-800 text-white" data-testid="partner-signup-goto-login">
            Sign in to your partner portal
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Become a Partner"
        description="Join the AdmissionCrafts partner network. Attractive commissions, real-time student tracking, monthly payouts, and counselling-ready predictions across India — zero infrastructure required."
        path="/partner-signup"
      />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <Link to="/partners" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to partners
        </Link>

        <h1 className="font-display text-3xl font-bold text-slate-900">Become a Partner</h1>
        <p className="text-slate-600 mt-2 max-w-xl">
          Create your partner account in 60 seconds. You&apos;ll sign in and complete KYC inside the partner portal. Your unique partner code is issued once an admin verifies your KYC.
        </p>

        <Card className="mt-8 p-6 sm:p-8">
          <form onSubmit={submit} className="space-y-5" data-testid="partner-signup-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Full name *</label>
                <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="As per official ID" className="mt-1" data-testid="signup-full-name" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email *</label>
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="mt-1" data-testid="signup-email" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mobile *</label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="98xxxxxxxx" className="mt-1" data-testid="signup-phone" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">City</label>
                <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Pune, Mumbai…" className="mt-1" data-testid="signup-city" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password *</label>
                <div className="relative mt-1">
                  <Input
                    type={showPwd ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="pr-10"
                    data-testid="signup-password"
                  />
                  <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">You&apos;ll use this to log into the partner portal.</p>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">I represent</label>
                <select
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  className="mt-1 w-full h-10 px-3 border border-slate-200 rounded-md text-sm"
                  data-testid="signup-category"
                >
                  <option value="">Select (optional)</option>
                  <option value="coaching_institute">Coaching institute</option>
                  <option value="independent_counsellor">Independent counsellor</option>
                  <option value="engineering_college">Engineering college</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy || !valid()}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="signup-submit"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account…</> : "Create partner account"}
            </Button>

            <p className="text-[11px] text-slate-500 text-center">
              Already a partner? <Link to="/portal/login" className="text-slate-900 font-semibold underline">Sign in</Link>
            </p>
          </form>
        </Card>

        {/* --- Why partner + FAQ (SEO content) ---
            Adds real, indexable copy so the page is not just a signup form.
            Keeps the form as the primary CTA above the fold.
        */}
        <section className="mt-14" data-testid="partner-signup-why">
          <h2 className="font-display text-2xl font-bold text-slate-900">Why partner with AdmissionCrafts?</h2>
          <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed">
            Every year, thousands of entrance-exam aspirants across India and their families walk into counselling
            with a handful of college names picked up from senior students, coaching flyers, or hurried Google
            searches. We built AdmissionCrafts to widen that shortlist with a data-grounded view of every seat a
            student could realistically secure — grouped as High Chance, Possible Chance, and Low Chance. Partnering
            with us extends that guidance through your existing student relationships without adding operational
            overhead.
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-display text-base font-semibold text-slate-900">Attractive per-profile commission</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                Earn a transparent share on every ₹99 prediction sold through your unique partner code. Commissions
                are auto-computed, visible in your partner dashboard in real time, and paid out monthly to the bank
                account you verify during KYC.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-display text-base font-semibold text-slate-900">Real-time student tracking</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                See which of your referred students have signed up, purchased a prediction, corrected their marks, or
                asked for a modification. Follow up when it matters — not with cold calls, but with genuine, timely
                guidance.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-display text-base font-semibold text-slate-900">Zero infrastructure investment</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                We host the prediction engine, the payment stack, the receipts and the support pipeline. Your only
                job is to share your partner code with parents and students who need guidance. Everything else is
                handled by AdmissionCrafts.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-display text-base font-semibold text-slate-900">Built for every exam we cover</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                Our data is refreshed each counselling cycle against the official publications of the authority
                that runs the exam — Maharashtra State CET Cell for CAP, MCC and State DME for NEET, and more
                on the roadmap. Students you refer get the same rigorous shortlist we publish to every direct
                customer — no watered-down versions.
              </p>
            </div>
          </div>
        </section>

        <section className="mt-14" data-testid="partner-signup-faq">
          <h2 className="font-display text-2xl font-bold text-slate-900">Frequently asked questions</h2>
          <div className="mt-6 space-y-6">
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">Who can become a partner?</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                Independent counsellors, coaching-class owners, career-guidance boutiques, and engineering-college
                outreach cells based anywhere in India are welcome. You do not need a formal registered entity — a
                verifiable PAN and a working bank account are enough for KYC.
              </p>
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">How long does approval take?</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                Most KYC reviews are completed within 24 to 48 hours of you submitting the documents. You will
                receive an email and an SMS the moment your unique partner code is activated.
              </p>
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">What documents do I need for KYC?</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                A government-issued ID (Aadhaar or PAN — last four digits and a masked image are sufficient), a
                cancelled cheque or bank statement showing your account and IFSC, and a phone number that can receive
                an OTP. Everything is uploaded via the encrypted partner-portal form after signup.
              </p>
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">When are commissions paid?</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                Commissions accumulate in your dashboard in real time and are settled every calendar month to the
                bank account you registered during KYC. You will get an SMS with the UTR the moment the payout is
                initiated.
              </p>
            </div>
            <div>
              <h3 className="font-display text-base font-semibold text-slate-900">Can I promote AdmissionCrafts on social media?</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">
                Yes — we encourage it. You will find a small kit of shareable graphics, sample WhatsApp messages, and
                explainer videos in the partner dashboard once your KYC is approved. Please stick to the wording we
                provide so students are not misled about what the service delivers.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
